import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Clock3, Loader2, Play } from 'lucide-react';
import type { ReadingAssessmentSummary } from './ReadingAssessmentStep';

type OptionKey = 'A' | 'B' | 'C' | 'D' | 'E';

type ReadingQuestion = {
  questionId: string;
  question: string;
  options: Record<OptionKey, string>;
  correctOption?: OptionKey;
};

type ReadingSpeedPhase = 'welcome' | 'reading' | 'paused' | 'questions';
type ReadingSpeedExternalAction = 'start' | 'pause' | 'resume' | 'finishReading' | 'completeQuestions';

interface ReadingSpeedStepProps {
  initialSpeed: number;
  onSpeedChange: (speed: number) => void;
  onNext: (payload: { speed: number; assessment?: ReadingAssessmentSummary | null }) => void;
  readingTitle?: string;
  sourceText?: string;
  sourceQuestions?: ReadingQuestion[];
  difficultyLevel?: string;
  sourceTextLoading?: boolean;
  startImmediately?: boolean;
  hideWelcome?: boolean;
  hideReadingActions?: boolean;
  hideQuestionActions?: boolean;
  hideReadingHeader?: boolean;
  externalControl?: { action: ReadingSpeedExternalAction; nonce: number } | null;
  onPhaseChange?: (phase: ReadingSpeedPhase) => void;
  onElapsedSecondsChange?: (elapsedSeconds: number) => void;
  onQuestionReadinessChange?: (canContinue: boolean) => void;
  fontSizeClass?: string;
  fontFamily?: string;
  accentColor?: string;
  fitContainer?: boolean;
}

const DEFAULT_SAMPLE_TEXT = `Okulun kütüphane kulübü, bu yıl öğrencilerin kitaplarla daha güçlü bir bağ kurabilmesi için yeni bir çalışma başlattı. Kulüp üyeleri önce sınıflardaki okuma alışkanlıklarını gözlemledi ve öğrencilerin en çok hangi tür metinlerden hoşlandığını anlamaya çalıştı. Yapılan kısa görüşmelerde bazı öğrenciler macera metinlerini severken bazıları bilim, tarih ve doğa konularına ilgi duyduklarını söyledi. Bunun üzerine kulüp, her yaş grubuna uygun metinlerin yer aldığı küçük okuma köşeleri hazırlamaya karar verdi.

Hazırlanan köşelerde yalnızca kitaplar değil, kısa bilgi panoları ve merak uyandıran notlar da bulunuyordu. Örneğin bir köşede göç eden kuşların nasıl yön bulduğunu anlatan bir yazı yer alırken başka bir köşede eski uygarlıkların şehir yaşamını tanıtan bir metin bulunuyordu. Kulüp üyeleri, öğrencilerin yalnızca metni okuyup geçmesini değil, okuduklarıyla ilgili düşünmelerini de istiyordu. Bu nedenle her metnin sonuna küçük hatırlatma soruları eklendi. Böylece öğrenciler hem daha dikkatli okumaya başladı hem de metnin ana fikrini bulma konusunda kendilerini geliştirdi.

Bir süre sonra öğretmenler de bu çalışmanın etkisini fark etti. Ders sırasında metin inceleme etkinlikleri yapıldığında öğrenciler daha hızlı odaklanıyor, önemli bilgileri daha kolay seçebiliyor ve sorulara daha düzenli cevap veriyordu. Kütüphane kulübü, düzenli okumanın yalnızca hız kazandırmadığını; dikkat, yorumlama ve anlama becerilerini de güçlendirdiğini görmüş oldu. Bu nedenle dönem sonunda herkes için açık olacak yeni bir okuma günü düzenlemeye karar verdiler.`;

const normalizeQuestions = (questions: ReadingQuestion[] = [], difficultyLevel?: string) => {
  const desiredOptionCount = ['Lise'].includes(String(difficultyLevel || '').trim()) ? 5 : 4;

  return questions
    .map((question, index) => {
      const options = (['A', 'B', 'C', 'D', 'E'] as const)
        .map((key) => ({
          key,
          text: String(question?.options?.[key] || '').trim(),
        }))
        .filter((option) => option.text)
        .slice(0, desiredOptionCount);

      if (desiredOptionCount === 5 && options.length === 4) {
        options.push({
          key: 'E',
          text: 'Metinde buna dair açık bir bilgi yer almamaktadır.',
        });
      }

      if (!question?.question || options.length < 2) return null;

      return {
        id: String(question.questionId || `q_${index + 1}`),
        prompt: String(question.question),
        options,
        correctKey: (question.correctOption || 'A') as OptionKey,
      };
    })
    .filter(Boolean) as Array<{
      id: string;
      prompt: string;
      options: Array<{ key: OptionKey; text: string }>;
      correctKey: OptionKey;
    }>;
};

const ReadingSpeedStep: React.FC<ReadingSpeedStepProps> = ({
  initialSpeed,
  onSpeedChange,
  onNext,
  readingTitle,
  sourceText,
  sourceQuestions = [],
  difficultyLevel,
  sourceTextLoading = false,
  startImmediately = false,
  hideWelcome = false,
  hideReadingActions = false,
  hideQuestionActions = false,
  hideReadingHeader = false,
  externalControl = null,
  onPhaseChange,
  onElapsedSecondsChange,
  onQuestionReadinessChange,
  fontSizeClass = 'text-[26px] leading-[1.65]',
  fontFamily = 'GoogleSans, Google Sans, sans-serif',
  accentColor = 'var(--theme-primary)',
  fitContainer = false,
}) => {
  const displayText = sourceText || DEFAULT_SAMPLE_TEXT;
  const wordCount = displayText.split(/\s+/).filter(Boolean).length;
  const normalizedQuestions = useMemo(
    () => normalizeQuestions(sourceQuestions, difficultyLevel),
    [difficultyLevel, sourceQuestions]
  );
  const [phase, setPhase] = useState<ReadingSpeedPhase>(() => (startImmediately ? 'reading' : 'welcome'));
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [answers, setAnswers] = useState<Record<string, OptionKey | undefined>>({});
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const canFinishReading = !sourceTextLoading;
  const allAnswered = normalizedQuestions.length > 0 && normalizedQuestions.every((question) => Boolean(answers[question.id]));
  const canContinue = normalizedQuestions.length === 0 || allAnswered;
  const safeActiveQuestionIndex = Math.min(activeQuestionIndex, Math.max(normalizedQuestions.length - 1, 0));
  const visibleQuestions = fitContainer
    ? normalizedQuestions.slice(safeActiveQuestionIndex, safeActiveQuestionIndex + 1)
    : normalizedQuestions;
  const canGoPreviousQuestion = safeActiveQuestionIndex > 0;
  const canGoNextQuestion = safeActiveQuestionIndex < normalizedQuestions.length - 1;
  const googleSansStyle = { fontFamily };
  const themeTitleStyle = { color: accentColor, ...googleSansStyle };
  const systemThemeTitleStyle = { color: 'var(--theme-primary)', ...googleSansStyle };

  useEffect(() => {
    if (phase !== 'reading') return;

    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [onPhaseChange, phase]);

  useEffect(() => {
    onElapsedSecondsChange?.(elapsedSeconds);
  }, [elapsedSeconds, onElapsedSecondsChange]);

  useEffect(() => {
    onQuestionReadinessChange?.(canContinue);
  }, [canContinue, onQuestionReadinessChange]);

  useEffect(() => {
    setActiveQuestionIndex(0);
  }, [readingTitle, sourceQuestions]);

  useEffect(() => {
    if (activeQuestionIndex <= Math.max(normalizedQuestions.length - 1, 0)) return;
    setActiveQuestionIndex(Math.max(normalizedQuestions.length - 1, 0));
  }, [activeQuestionIndex, normalizedQuestions.length]);

  const handleStart = () => {
    if (phase === 'paused') {
      setPhase('reading');
      return;
    }
    setElapsedSeconds(0);
    setAnswers({});
    setPhase('reading');
  };

  const handleFinishReading = () => {
    if (!canFinishReading) return;
    setPhase('questions');
  };

  useEffect(() => {
    if (!externalControl) return;

    if (externalControl.action === 'start' || externalControl.action === 'resume') {
      handleStart();
      return;
    }

    if (externalControl.action === 'pause' && phase === 'reading') {
      setPhase('paused');
      return;
    }

    if (externalControl.action === 'finishReading') {
      handleFinishReading();
      return;
    }

    if (externalControl.action === 'completeQuestions') {
      handleComplete();
    }
  }, [externalControl?.nonce]);

  const handleComplete = () => {
    if (!canContinue) return;

    const measuredSeconds = Math.max(elapsedSeconds, 1);
    const measuredSpeed = Math.min(1000, Math.max(60, Math.round((wordCount / measuredSeconds) * 60)));
    const correctCount = normalizedQuestions.filter((question) => answers[question.id] === question.correctKey).length;
    const score = normalizedQuestions.length > 0
      ? Math.round((correctCount / normalizedQuestions.length) * 100)
      : 0;

    onSpeedChange(measuredSpeed);
    onNext({
      speed: measuredSpeed,
      assessment: {
        assessmentId: String(readingTitle || 'initial-reading-test'),
        age: ['İlkokul', 'Ortaokul', 'Lise'].includes(String(difficultyLevel || '').trim())
          ? ({ 'İlkokul': 10, 'Ortaokul': 13, 'Lise': 16 } as Record<string, number>)[String(difficultyLevel || '').trim()] || 13
          : 13,
        score,
        recommendedReadingLevel: difficultyLevel || null,
        textType: 'öykü',
      },
    });
  };

  return (
    <div className={`flex w-full flex-col items-center ${fitContainer ? 'h-full min-h-0 overflow-hidden' : ''}`}>
      {phase === 'welcome' && !hideWelcome && (
        <div className="w-full max-w-4xl rounded-[28px] border border-gray-200 bg-white px-8 py-12 text-center shadow-sm">
          <h2 className="text-[2rem] sm:text-[2.5rem] leading-tight font-bold" style={themeTitleStyle}>
            Hızlı Okuma ve Anlama Ölçüm Testi
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Hoş geldiniz. Hazırsanız ‘Hızlı Okuma ve Anlama Ölçüm Testi’ni başlatınız.
          </p>
          <button
            onClick={handleStart}
            disabled={sourceTextLoading}
            className="mt-8 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-base font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: 'var(--theme-primary)' }}
          >
            <Play className="w-4 h-4" />
            Başlat
          </button>
        </div>
      )}

      {(phase === 'reading' || phase === 'paused') && (
        <div className={`w-full ${fitContainer ? 'flex h-full min-h-0 flex-col overflow-hidden' : ''}`}>
          {!hideReadingHeader && (
            <div className="mb-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <div>
                <h2 className="text-[1.9rem] font-bold leading-tight" style={themeTitleStyle}>
                  {phase === 'paused' ? 'Okuma Duraklatıldı' : 'Metni okuyunuz'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{readingTitle || 'Okuma Metni'}</p>
              </div>
              <div className="min-w-[176px] rounded-2xl border border-[#e0eaee] bg-[#f8fbfc] px-5 py-4">
                <div className="flex items-center gap-2 text-sm font-black text-[#7b909b]">
                  <Clock3 className="h-4 w-4" />
                  Kronometre
                </div>
                <div className="mt-2 text-[34px] font-black leading-none" style={{ color: accentColor }}>
                  {String(Math.floor(elapsedSeconds / 60)).padStart(2, '0')}:{String(elapsedSeconds % 60).padStart(2, '0')}
                </div>
              </div>
            </div>
          )}

          {/* Uzun metinler kutuya sığmıyordu ve overflow-hidden yüzünden görünmeden kırpılıyordu
              (413 kelimelik metnin ~270'i görünüyordu). Sığmayan metin artık kaydırılabiliyor. */}
          <div className={`${fitContainer ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pb-2' : 'min-h-[620px] px-2 pb-8'} bg-white text-[#111111]`}>
            {sourceTextLoading ? (
              <div className={`flex flex-col items-center justify-center gap-3 ${fitContainer ? 'h-full min-h-0' : 'min-h-[320px]'}`}>
                <Loader2 className="w-7 h-7 animate-spin" style={{ color: accentColor }} />
                <p className="text-sm font-medium text-gray-500">Ölçüm metni yükleniyor...</p>
              </div>
            ) : (
              displayText.split('\n\n').map((paragraph, index) => (
                <p
                  key={`${index}-${paragraph.slice(0, 20)}`}
                  className={`${fontSizeClass} ${index > 0 ? (fitContainer ? 'mt-[clamp(0.75rem,2vmin,2rem)]' : 'mt-8') : ''}`}
                  style={{ fontFamily }}
                >
                  {paragraph}
                </p>
              ))
            )}
          </div>

          {!hideReadingActions && (
            <button
              onClick={handleFinishReading}
              disabled={!canFinishReading}
              className="mt-6 w-full flex items-center justify-center gap-2 py-3 text-white rounded-lg font-medium shadow-md disabled:opacity-50"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              Metni Bitirdim
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {phase === 'questions' && (
        <div className={`w-full ${fitContainer ? 'flex h-full min-h-0 flex-col overflow-hidden' : ''}`}>
          <div className={`${fitContainer ? 'mb-3 px-4 py-3' : 'mb-5 px-6 py-5'} rounded-2xl border border-slate-200 bg-white`}>
            <h2 className={`${fitContainer ? 'text-[1.35rem]' : 'text-[1.85rem]'} font-bold leading-tight`} style={systemThemeTitleStyle}>Anlama Soruları</h2>
            <p className={`${fitContainer ? 'mt-1 text-xs' : 'mt-2 text-sm'} text-slate-500`}>
              Okuma süreniz kaydedildi. Şimdi metinle ilgili soruları cevaplayınız.
            </p>
          </div>

          <div className={`${fitContainer ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : 'space-y-5'}`}>
            {visibleQuestions.map((question, index) => {
              const questionIndex = fitContainer ? safeActiveQuestionIndex : index;

              return (
              <div key={question.id} className={`${fitContainer ? 'rounded-xl p-3' : 'rounded-2xl p-5'} border border-slate-200 bg-white`}>
                <div className={`${fitContainer ? 'text-xs' : 'text-sm'} font-semibold text-slate-500`}>Soru {questionIndex + 1}</div>
                <h4 className={`${fitContainer ? 'mt-2 text-[clamp(1rem,2.2vmin,1.45rem)] leading-snug' : 'mt-1 text-lg'} font-bold text-slate-900`}>{question.prompt}</h4>
                <div className={`${fitContainer ? 'mt-4 gap-3' : 'mt-4 gap-3'} grid grid-cols-1 md:grid-cols-2`}>
                  {question.options.map((option) => {
                    const isSelected = answers[question.id] === option.key;
                    return (
                      <button
                        key={option.key}
                        onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: option.key }))}
                        className={`${fitContainer ? 'rounded-lg px-3 py-2' : 'rounded-xl px-4 py-3'} border-2 text-left transition-all`}
                        style={{
                          borderColor: isSelected ? 'color-mix(in srgb, var(--theme-primary) 26%, white)' : '#e5e7eb',
                          backgroundColor: isSelected ? 'color-mix(in srgb, var(--theme-primary) 8%, white)' : '#fff',
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-black" style={{ color: isSelected ? 'var(--theme-primary)' : '#64748b' }}>{option.key}</span>
                          <span className={`${fitContainer ? 'text-[clamp(0.92rem,1.8vmin,1.1rem)] leading-snug' : 'text-sm'} text-slate-700`} style={{ fontFamily }}>{option.text}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>

          {fitContainer && normalizedQuestions.length > 0 && (
            <div className="mt-3 flex shrink-0 flex-col items-center gap-3 border-t border-slate-100 pt-3">
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setActiveQuestionIndex((prev) => Math.max(0, prev - 1))}
                  disabled={!canGoPreviousQuestion}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-35"
                  aria-label="Önceki soru"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center justify-center gap-3">
                  {normalizedQuestions.map((question, index) => {
                    const isActive = index === safeActiveQuestionIndex;
                    const isAnswered = Boolean(answers[question.id]);

                    return (
                      <button
                        key={question.id}
                        type="button"
                        onClick={() => setActiveQuestionIndex(index)}
                        className={`flex h-10 min-w-[2.5rem] items-center justify-center rounded-md border px-3 text-sm font-bold transition-colors ${isActive
                          ? 'border-transparent text-white shadow-sm'
                          : isAnswered
                            ? 'border-[#b9d9d6] bg-[#eefafa] text-[#23756f]'
                            : 'border-transparent bg-transparent text-slate-500 hover:bg-slate-100'
                          }`}
                        style={isActive ? { backgroundColor: 'var(--theme-primary)' } : undefined}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => setActiveQuestionIndex((prev) => Math.min(normalizedQuestions.length - 1, prev + 1))}
                  disabled={!canGoNextQuestion}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-35"
                  aria-label="Sonraki soru"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

            </div>
          )}

          {!hideQuestionActions && (
            <button
              onClick={handleComplete}
              disabled={!canContinue}
              className="mt-6 w-full flex items-center justify-center gap-2 py-3 text-white rounded-lg font-medium shadow-md disabled:opacity-50"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              Devam Et
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReadingSpeedStep;
